import { useCallback, useRef } from 'react';

export type MenuTarget = { kind: 'section' | 'account'; id: string };
export type OpenMenu = (target: MenuTarget, x: number, y: number) => void;

const HOLD_MS = 500;
const MOVE_SLOP = 10;

/**
 * Opens a menu on right-click (desktop) or a long press (touch, where the
 * sidebar's drag and drop does not work). Returns handlers to spread on the
 * pressed element. A long press swallows the click that follows it, so the row
 * under the finger does not also navigate.
 */
export function usePressMenu(open: OpenMenu) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fired = useRef(false);
  const origin = useRef({ x: 0, y: 0 });

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  return useCallback(
    (target: MenuTarget) => ({
      onContextMenu: (e: React.MouseEvent) => {
        e.preventDefault();
        cancel();
        open(target, e.clientX, e.clientY);
      },
      onTouchStart: (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        fired.current = false;
        origin.current = { x: touch.clientX, y: touch.clientY };
        cancel();
        timer.current = setTimeout(() => {
          fired.current = true;
          navigator.vibrate?.(10);
          open(target, origin.current.x, origin.current.y);
        }, HOLD_MS);
      },
      onTouchMove: (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch && Math.hypot(touch.clientX - origin.current.x, touch.clientY - origin.current.y) > MOVE_SLOP) cancel();
      },
      onTouchEnd: cancel,
      onTouchCancel: cancel,
      onClickCapture: (e: React.MouseEvent) => {
        if (!fired.current) return;
        fired.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
    }),
    [open, cancel],
  );
}
